import React, { useState, useMemo } from 'react';
import { TimeEntry, WorkRolesDict, WorkTypesDict, ChargeCodesDict, WorkRoleValidationsList } from '../types/api.types';
import TimeEntryRow from './TimeEntryRow';
import './MemberEntryCard.css';

type SortField = 'date' | 'chargeTo' | 'hours' | 'workRole' | 'workType';
type SortDirection = 'asc' | 'desc';

interface MemberEntryCardProps {
  memberName: string;
  memberIdentifier: string;
  entries: TimeEntry[];
  workRoles: WorkRolesDict;
  workTypes: WorkTypesDict;
  chargeCodes: ChargeCodesDict;
  workRoleValidations: WorkRoleValidationsList;
  onEntryDeleted: (id: number) => void;
  onEntryUpdated: () => void;
  onAddEntry: (memberIdentifier: string) => void;
  onCopyFrom: (entry: TimeEntry) => void;
  sheetStatus?: string;
  timeDetailSignal?: { action: 'show' | 'hide'; epoch: number };
}

const MemberEntryCard: React.FC<MemberEntryCardProps> = ({
  memberName,
  memberIdentifier,
  entries,
  workRoles,
  workTypes,
  chargeCodes,
  workRoleValidations,
  onEntryDeleted,
  onEntryUpdated,
  onAddEntry,
  onCopyFrom,
  sheetStatus,
  timeDetailSignal,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getChargeToValue = (entry: TimeEntry): string => {
    if (entry.chargeToType === 'ChargeCode') {
      const chargeCode = chargeCodes[entry.chargeToId];
      if (chargeCode) {
        return `${chargeCode.company?.name || ''} / ${chargeCode.name}`;
      }
      return `Charge Code #${entry.chargeToId}`;
    } else {
      const parts = [
        entry.company?.name,
        entry.project?.name,
        entry.phase?.name,
        entry.ticket?.summary,
      ].filter(Boolean);
      return parts.join(' / ');
    }
  };

  const sortedEntries = useMemo(() => {
    if (!sortField) return entries;

    return [...entries].sort((a, b) => {
      let aValue: string | number = '';
      let bValue: string | number = '';

      switch (sortField) {
        case 'date':
          aValue = a.timeStart || '';
          bValue = b.timeStart || '';
          break;
        case 'chargeTo':
          aValue = getChargeToValue(a);
          bValue = getChargeToValue(b);
          break;
        case 'hours':
          aValue = a.actualHours;
          bValue = b.actualHours;
          break;
        case 'workRole':
          aValue = workRoles[a.workRole?.id ?? 0]?.name ?? '';
          bValue = workRoles[b.workRole?.id ?? 0]?.name ?? '';
          break;
        case 'workType':
          aValue = workTypes[a.workType?.id ?? 0]?.name ?? '';
          bValue = workTypes[b.workType?.id ?? 0]?.name ?? '';
          break;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortDirection === 'asc' ? comparison : -comparison;
      } else {
        const comparison = (aValue as number) - (bValue as number);
        return sortDirection === 'asc' ? comparison : -comparison;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries, sortField, sortDirection, workRoles, workTypes, chargeCodes]);

  // Group entries by date using a Map to avoid duplicate date groups
  const entriesByDate = useMemo(() => {
    const groupMap = new Map<string, { date: string; entries: TimeEntry[]; totalHours: number }>();

    sortedEntries.forEach((entry) => {
      const entryDate = entry.timeStart ? entry.timeStart.split('T')[0] : 'No Date';
      if (!groupMap.has(entryDate)) {
        groupMap.set(entryDate, { date: entryDate, entries: [], totalHours: 0 });
      }
      const group = groupMap.get(entryDate)!;
      group.entries.push(entry);
      group.totalHours += entry.actualHours || 0;
    });

    // Sort date groups; respect sort direction when sorting by date
    const direction = sortField === 'date' ? sortDirection : 'asc';
    return Array.from(groupMap.values()).sort((a, b) => {
      if (a.date === 'No Date') return 1;
      if (b.date === 'No Date') return -1;
      const cmp = a.date.localeCompare(b.date);
      return direction === 'asc' ? cmp : -cmp;
    });
  }, [sortedEntries, sortField, sortDirection]);

  const getSortIndicator = (field: SortField) => {
    if (sortField !== field) return '';
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  const formatDateLabel = (dateStr: string) => {
    if (dateStr === 'No Date') return dateStr;
    try {
      const date = new Date(dateStr + 'T00:00:00');
      return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="member-entry-card">
      <div className="card-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="header-left">
          <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
          <h3 className="member-name">{memberName}</h3>
          <span className="entry-count">({entries.length} entries)</span>
        </div>
        <button
          className="btn btn-primary btn-small"
          onClick={(e) => {
            e.stopPropagation();
            onAddEntry(memberIdentifier);
          }}
        >
          + Add Entry
        </button>
      </div>

      {isExpanded && (
        <div className="card-content">
          <table className="entries-table">
            <thead>
              <tr>
                <th className="sortable" onClick={() => handleSort('date')}>
                  Date{getSortIndicator('date')}
                </th>
                <th className="sortable" onClick={() => handleSort('chargeTo')}>
                  Charge To{getSortIndicator('chargeTo')}
                </th>
                <th className="sortable" onClick={() => handleSort('hours')}>
                  Hours{getSortIndicator('hours')}
                </th>
                <th>Department</th>
                <th className="sortable" onClick={() => handleSort('workRole')}>
                  Work Role{getSortIndicator('workRole')}
                </th>
                <th className="sortable" onClick={() => handleSort('workType')}>
                  Work Type{getSortIndicator('workType')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {entriesByDate.map((group) => (
                <React.Fragment key={group.date}>
                  <tr className="date-group-row">
                    <td colSpan={7}>
                      <span className="date-group-label">{formatDateLabel(group.date)}</span>
                      <span className="date-group-hours">{group.totalHours.toFixed(2)} hrs</span>
                    </td>
                  </tr>
                  {group.entries.map((entry) => (
                    <TimeEntryRow
                      key={entry.id}
                      entry={entry}
                      workRoles={workRoles}
                      workTypes={workTypes}
                      chargeCodes={chargeCodes}
                      workRoleValidations={workRoleValidations}
                      onDelete={onEntryDeleted}
                      onUpdate={onEntryUpdated}
                      onCopyFrom={onCopyFrom}
                      sheetStatus={sheetStatus}
                      timeDetailSignal={timeDetailSignal}
                    />
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MemberEntryCard;
